import React, { useState, useEffect, useRef, useCallback } from 'react';
import Select from 'react-select';

import ScopeStyle from '../css/Scope.module.css'

import { API_DEFAULT } from "../services/settings";
import { API } from "../services/routes";
import { request, tokenExpired, hasCode } from "../services/http";
import { uploadSummary, GENERIC_UPLOAD_FAILURE } from "../services/uploads";
import { scopeRefusal } from "../services/scopeErrors";
import { isCompleteRange } from "../services/dateRange";
import { channelProgramLimit, combinedOffered, isJoined, netReachRefusal } from "../services/netReach";
import { DOCUMENTS, fetchDocument } from "../services/documents";
import { saveAs } from "file-saver";

// A date input's min or max: undefined while the range is unknown (not loaded
// yet, or its request failed). An invalid Date's toJSON() is null, and slicing
// it during render unmounted the whole application.
const toDateInputValue = (value) => {
    const date = new Date(value);
    return isNaN(date) ? undefined : date.toJSON().slice(0, 10);
};

function Scope(props) {

    const { setAccessToken, accessToken, getAccessTokenWithRefresh, setShowLoader, setShowAlert, setMessage, setSeverity } = props

    // Refs rather than document.getElementById
    const fileInputRef = useRef(null);

    // What a load reads when it runs, never what makes it run: listing the
    // token would ask for both lists again on every refresh of it (every five
    // minutes of use), and listing the option would reload the channels when
    // Reach neto is chosen, which it has never done.
    const tokenRef = useRef(accessToken);
    tokenRef.current = accessToken;
    const optionRef = useRef();

    // Opens the file dialog of the hidden input.
    function openFileInput() {
        if (fileInputRef.current) fileInputRef.current.click();
    }

    // The files chosen so far, from the dialog or from a drop.
    const [filesToUpload, setFilesToUpload] = useState([]); // File[]

    // dragover handler
    function handleDragOver(evt) {
        evt.stopPropagation();
        evt.preventDefault();
        evt.dataTransfer.dropEffect = 'copy';
    }

    // Files chosen, whether from the dialog or dropped on the zone.
    function handleFileSelect(evt) {
        evt.stopPropagation();
        evt.preventDefault();

        const inputEl = fileInputRef.current;

        const incoming = evt.dataTransfer ? evt.dataTransfer.files : evt.target.files;
        const asArray = Array.from(incoming || []);
        const onlyXlsx = asArray.filter(f => /\.xlsx$/i.test(f.name));

        if (onlyXlsx.length !== asArray.length) {
            setShowLoader(false);
            setMessage("El formato de archivo no es válido. Solo .xlsx");
            setSeverity("danger");
            setTimeout(() => { setShowAlert(true) }, 0);
        }

        const next = [...filesToUpload, ...onlyXlsx];

        // quitar duplicados (nombre+size)
        const uniq = [];
        const seen = new Set();
        for (const f of next) {
            const key = `${f.name}__${f.size}`;
            if (!seen.has(key)) { seen.add(key); uniq.push(f); }
        }

        if (uniq.length === 0) {
            setFilesToUpload([]);
            if (inputEl) inputEl.value = "";
            return;
        }
        if (uniq.length > 5) {
            setShowLoader(false);
            setMessage("Máximo 5 archivos.");
            setSeverity("danger");
            setTimeout(() => { setShowAlert(true) }, 0);
            uniq.splice(5);
        }

        setFilesToUpload(uniq);

        // A drop leaves the input holding whatever was chosen before it
        if (inputEl && evt.dataTransfer) {
            inputEl.value = "";
        }
    }

    // Takes one file off the list. The input is cleared too: otherwise
    // choosing that same file again in the dialog fires no change.
    function removeFile(file) {
        setFilesToUpload(filesToUpload.filter(f => f !== file));
        if (fileInputRef.current) fileInputRef.current.value = "";
    }

    const dOWList = [
        { value: 0, label: "Lunes" },
        { value: 1, label: "Martes" },
        { value: 2, label: "Miércoles" },
        { value: 3, label: "Jueves" },
        { value: 4, label: "Viernes" },
        { value: 5, label: "Sábado" },
        { value: 6, label: "Domingo" }
    ];

    const targets = ['Hombre', 'Mujer', 'Menos 17', '18-24', '25-34', '35-44', '45-54', 'Más 55',
        'Antioquia', 'Cafe', 'Caribe', 'Centro', 'Oriente', 'Pacifico', 'NSE 1', 'NSE 2',
        'NSE 3', 'NSE 4', 'NSE 5', 'NSE 6', 'Factor Abierta', 'Factor Cerrada', 'Comprador ppal', 'Otro rol'];

    const populationList = [
        { value: "accounts", label: "Hogares" },
        { value: "people", label: "Personas" },
        { value: "both", label: "Ambos" }
    ];

    const [evaluationOption, setEvaluationOption] = useState('post')
    const [universeOption, setUniverseOption] = useState('new')
    const [weightOption, setWeightOption] = useState('last_day')
    const [lowerPreDate, setLowerPreDate] = useState();
    const [upperPreDate, setUpperPreDate] = useState();

    const [selectedOption, setSelectedOption] = useState('evaluacion-pauta')
    optionRef.current = selectedOption
    const [customTarget, setCustomTarget] = useState(false)
    const [kindOption, setKindOption] = useState('program_name')
    const [tableOption, setTableOption] = useState('rcn')
    const [lowerDate, setLowerDate] = useState();
    const [upperDate, setUpperDate] = useState();
    const [dOWOptions, setDOWOptions] = useState(dOWList);
    const [targetSelected, setTargetSelected] = useState(targets.map((v, i) => {
        return ({ value: i, label: v })
    }));
    const [selectedChannelPrograms, setSelectedChannelPrograms] = useState([]);
    const [populationOption, setPopulationOption] = useState();

    const [channelPrograms, setChannelPrograms] = useState([])
    const [unifiedPrograms, setUnifiedPrograms] = useState(false)
    const [combinedPrograms, setCombinedPrograms] = useState(false)
    // What the reach neto rules read (services/netReach.js).
    const netReachForm = { universe: universeOption, kind: kindOption, combined: combinedPrograms }

    const [dataRangeDates, setDataRangeDates] = useState({})

    // A range is chosen: the Modelo 1 channels are asked for by it. Only a
    // whole one, not every keystroke of a year (services/dateRange.js).
    const hasDateRange = isCompleteRange(lowerDate, upperDate)

    // The last request for the channels. Answers can arrive out of order, and
    // only the last one's may draw the list or the alert.
    const channelsAsked = useRef(0)

    // The dates the consumption table covers, which bound the date inputs.
    const getDateRange = useCallback(async (access = tokenRef.current, afterRefresh = false) => {
        const answer = await request(
            universeOption === 'new' ? `${API_DEFAULT}/${API.rangeDatesNu}?from_table=${tableOption}` : `${API_DEFAULT}/${API.rangeDates}`, {
            headers: { "Authorization": "Bearer " + access }
        });
        if (tokenExpired(answer) && !afterRefresh) {
            let newAccess = await getAccessTokenWithRefresh();
            if (newAccess) {
                setAccessToken(newAccess);
                localStorage.setItem('accessToken', newAccess);
                // Once: the token is no longer what makes this run again.
                return getDateRange(newAccess, true);
            }
        } else if (answer.ok) {
            setDataRangeDates(answer.data)
        }
        // On failure the previous range stays. No alert: this runs on every
        // page load, for users who never open the form.
    }, [universeOption, tableOption, getAccessTokenWithRefresh, setAccessToken])

    const getChannelPrograms = useCallback(async (access = tokenRef.current, afterRefresh = false) => {
        if (universeOption !== 'new' || hasDateRange) {
            // The loader only while the reach neto form is the one chosen, as
            // before, when this read the DOM for its section.
            const loader = optionRef.current === 'reach-neto';
            if (loader) { setShowLoader(true) };
            let url = universeOption === 'new'
                ? (unifiedPrograms ? `${API_DEFAULT}/${API.channelProgramAlterNu}?from_table=${tableOption}` : `${API_DEFAULT}/${API.channelProgramNu}?from_table=${tableOption}`)
                : (unifiedPrograms ? `${API_DEFAULT}/${API.channelProgramAlter}` : `${API_DEFAULT}/${API.channelProgram}`);

            if (hasDateRange && universeOption === 'new') {
                url += `&lower_date=${lowerDate}&upper_date=${upperDate}`;
            }

            const asked = ++channelsAsked.current;
            const answer = await request(url, { headers: { "Authorization": "Bearer " + access } });
            if (asked !== channelsAsked.current) {
                // A later request owns the list and the loader.
                return;
            }
            const channels = answer.data && answer.data['canal_name'];

            if (tokenExpired(answer) && !afterRefresh) {
                let newAccess = await getAccessTokenWithRefresh();
                if (newAccess) {
                    setAccessToken(newAccess);
                    localStorage.setItem('accessToken', newAccess);
                    // Once: the token is no longer what makes this run again.
                    return getChannelPrograms(newAccess, true);
                }
                if (loader) { setShowLoader(false) };
            } else if (answer.ok && Array.isArray(channels)) {
                setChannelPrograms(channels.map((v, i) => ({ value: i, label: v })));
                if (loader) { setShowLoader(false) };
            } else if (loader) {
                // Only when the reach neto form is open; the load in the
                // background fails silently, like the date range.
                setShowLoader(false);
                setMessage("No fue posible cargar los canales y programas. Intente de nuevo.");
                setSeverity("danger");
                setTimeout(() => { setShowAlert(true) }, 0);
            }
        }
    }, [universeOption, unifiedPrograms, tableOption, lowerDate, upperDate, hasDateRange,
        getAccessTokenWithRefresh, setAccessToken, setShowLoader, setMessage, setSeverity, setShowAlert])

    const downloadChannelProgramList = async (access = accessToken) => {
        setShowLoader(true)
        const answer = await request(
            universeOption === 'new' ? `${API_DEFAULT}/${API.channelProgramCleanNu}` : `${API_DEFAULT}/${API.channelProgramClean}`,
            { headers: { "Authorization": "Bearer " + access } }
        );
        const data = answer.data;

        if (tokenExpired(answer)) {
            let newAccess = await getAccessTokenWithRefresh();
            if (newAccess) {
                setAccessToken(newAccess);
                localStorage.setItem('accessToken', newAccess);
                downloadChannelProgramList(newAccess)
            }
        } else if (!answer.ok || !Array.isArray(data) || data.length === 0) {
            setShowLoader(false)
            setMessage("No fue posible descargar el listado de canales y programas. Intente de nuevo.")
            setSeverity("danger")
            setTimeout(() => { setShowAlert(true) }, 0)
        } else {
            let csvContent = "data:text/csv;charset=utf-8,";
            csvContent += Object.keys(data[0]).join(",") + "\n";
            data.forEach(row => {
                csvContent += `"${Object.values(row).join('","')}"\n`
            });
            let element = document.createElement('a');
            element.setAttribute('href', encodeURI(csvContent));
            element.setAttribute('download', universeOption === 'new' ? "listado_names_modelo_1.csv" : "listado_names.csv");
            document.body.appendChild(element);
            element.click();
            document.body.removeChild(element);
            setShowLoader(false)
        }
    }

    // Each load runs when what it asks for changes, and once when the form is
    // mounted with the portal. Both read the token when they run.
    useEffect(() => {
        getChannelPrograms();
    }, [getChannelPrograms]);

    useEffect(() => {
        getDateRange();
    }, [getDateRange]);

    const hideScope = () => {
        setEvaluationOption('post')
        setUniverseOption('new')
        setWeightOption('last_day')
        setLowerPreDate()
        setUpperPreDate()

        setSelectedOption('evaluacion-pauta')
        setCustomTarget(false)
        setKindOption('program_name')
        setTableOption('rcn')
        setLowerDate()
        setUpperDate()
        setDOWOptions(dOWList)
        setTargetSelected(targets.map((v, i) => ({ value: i, label: v })));
        setSelectedChannelPrograms([])
        setUnifiedPrograms(false)
        setCombinedPrograms(false)
        setPopulationOption()
        setFilesToUpload([])

        const scope = document.getElementById('scope')
        scope.classList.remove(ScopeStyle["hide-scope"])
        scope.classList.toggle(ScopeStyle["show-scope"])

        const emailEl = document.getElementById("email");
        if (emailEl) emailEl.value = "";
        if (fileInputRef.current) fileInputRef.current.value = "";
    }

    const generateReach = async (access = accessToken) => {
        setShowLoader(true)

        let body = new FormData();
        body.append("email", document.getElementById("email").value)

        if (universeOption === "new") {
            body.append("universe", 1)
            body.append("kind", kindOption)
            body.append("weight_type", weightOption)
            body.append("from_table", tableOption)
        }

        if (body.get('email') === '') {
            setShowLoader(false);
            setMessage("El email no puede estar vacío");
            setSeverity("danger");
            setTimeout(() => { setShowAlert(true) }, 0);
        }
        else if (selectedChannelPrograms.length === 0) {
            setShowLoader(false);
            setMessage("El programa no puede estar vacío");
            setSeverity("danger");
            setTimeout(() => { setShowAlert(true) }, 0);
        }
        else if (netReachRefusal(selectedChannelPrograms.map((v) => v["label"]), netReachForm)) {
            setShowLoader(false);
            setMessage(netReachRefusal(selectedChannelPrograms.map((v) => v["label"]), netReachForm));
            setSeverity("danger");
            setTimeout(() => { setShowAlert(true) }, 0);
        }
        else if (lowerDate === undefined || lowerDate === '') {
            setShowLoader(false);
            setMessage("La fecha inicial no puede estar vacío");
            setSeverity("danger");
            setTimeout(() => { setShowAlert(true) }, 0);
        }
        else if (upperDate === undefined || upperDate === '') {
            setShowLoader(false);
            setMessage("La fecha final no puede estar vacío");
            setSeverity("danger");
            setTimeout(() => { setShowAlert(true) }, 0);
        }
        else if (dOWOptions.length === 0) {
            setShowLoader(false);
            setMessage("Los días de la semana no pueden estar vacíos");
            setSeverity("danger");
            setTimeout(() => { setShowAlert(true) }, 0);
        }
        else if (populationOption === undefined || populationOption === null) {
            setShowLoader(false);
            setMessage("La población no puede estar vacía");
            setSeverity("danger");
            setTimeout(() => { setShowAlert(true) }, 0);
        }
        else if (body.get('email') !== '') {
            const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

            if (regex.test(body.get('email')) === false) {
                setShowLoader(false);
                setMessage("Ha ingresado un correo inválido");
                setSeverity("danger");
                setTimeout(() => { setShowAlert(true) }, 0);
            }
            else if (lowerDate > upperDate) {
                setShowLoader(false);
                setMessage("La fecha inicial debe ser menor o igual a la final");
                setSeverity("danger");
                setTimeout(() => { setShowAlert(true) }, 0);
            }
            else {
                body.append("canal_name", selectedChannelPrograms.map((v) => v["label"]).join(";;"))
                body.append("lower_date", lowerDate)
                body.append("upper_date", upperDate)
                body.append("days_of_week", dOWOptions.map((v) => v["value"]).join(" "))
                body.append("together", unifiedPrograms ? "True" : "False")
                body.append("joined", isJoined(netReachForm) ? "True" : "False")
                body.append("group", populationOption["value"])

                const answer = await request(
                    `${API_DEFAULT}/${API.netReach}`, {
                    method: 'POST',
                    headers: { "Authorization": "Bearer " + access },
                    body
                });
                if (tokenExpired(answer)) {
                    let newAccess = await getAccessTokenWithRefresh();
                    if (newAccess) {
                        setAccessToken(newAccess);
                        localStorage.setItem('accessToken', newAccess);
                        generateReach(newAccess);
                    }
                }
                else if (answer.ok && hasCode(answer, 'consult_created')) {
                    setShowLoader(false);
                    hideScope()
                    setMessage("La consulta ha sido creada. Pronto al correo " + document.getElementById("email").value + " recibirá el reach neto generado.");
                    setSeverity("success");
                    setTimeout(() => { setShowAlert(true) }, 0);
                }
                else {
                    setShowLoader(false);
                    setMessage("Error");
                    setSeverity("danger");
                    setTimeout(() => { setShowAlert(true) }, 0);
                }
            }
        }
    }

    // One request per file, in order: the back takes one workbook at a time.
    const generateScope = async (access = accessToken) => {
        setShowLoader(true);

        const emailValue = document.getElementById("email").value;

        if (emailValue === '') {
            setShowLoader(false);
            setMessage("El email no puede estar vacío");
            setSeverity("danger");
            setTimeout(() => { setShowAlert(true) }, 0);
            return;
        }
        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!regex.test(emailValue)) {
            setShowLoader(false);
            setMessage("Ha ingresado un correo inválido");
            setSeverity("danger");
            setTimeout(() => { setShowAlert(true) }, 0);
            return;
        }

        if (evaluationOption === "pre") {
            if (!lowerPreDate) {
                setShowLoader(false);
                setMessage("La fecha inicial no puede estar vacía");
                setSeverity("danger");
                setTimeout(() => { setShowAlert(true) }, 0);
                return;
            }
            if (!upperPreDate) {
                setShowLoader(false);
                setMessage("La fecha final no puede estar vacía");
                setSeverity("danger");
                setTimeout(() => { setShowAlert(true) }, 0);
                return;
            }
            if (lowerPreDate > upperPreDate) {
                setShowLoader(false);
                setMessage("La fecha inicial debe ser menor o igual a la final");
                setSeverity("danger");
                setTimeout(() => { setShowAlert(true) }, 0);
                return;
            }
        }

        if (selectedOption === "evaluacion-pauta") {
            if (!filesToUpload || filesToUpload.length === 0) {
                setShowLoader(false);
                setMessage("No se ha seleccionado ningún archivo");
                setSeverity("danger");
                setTimeout(() => { setShowAlert(true) }, 0);
                return;
            }
            if (filesToUpload.length > 5) {
                setShowLoader(false);
                setMessage("Máximo 5 archivos");
                setSeverity("danger");
                setTimeout(() => { setShowAlert(true) }, 0);
                return;
            }
        }

        // The rows the back says it could not find, as the file the client is
        // told to look at.
        const downloadProgramErrors = (rows) => {
            let csvContent = "data:text/csv;charset=utf-8,";
            csvContent += Object.keys(rows[0]).join(",") + "\n";
            rows.forEach(row => {
                csvContent += `"${Object.values(row).join('","')}"\n`;
            });
            const element = document.createElement('a');
            element.setAttribute('href', encodeURI(csvContent));
            element.setAttribute('download', "error_names.csv");
            document.body.appendChild(element);
            element.click();
            document.body.removeChild(element);
        };

        const uploadOne = async (file, accessTokenLocal) => {
            const body = new FormData();
            body.append("email", emailValue);
            body.append("template", file);
            body.append("type", evaluationOption);

            if (universeOption === "new") {
                body.append("universe", 1);
                body.append("weight_type", weightOption);
            }
            if (evaluationOption === "pre") {
                body.append("lower_date", lowerPreDate);
                body.append("upper_date", upperPreDate);
            }
            if (customTarget && targetSelected.length > 0) {
                body.append("custom_target", '"' + targetSelected.map((v) => v["label"]).join('" "') + '"');
            }

            const answer = await request(`${API_DEFAULT}/${API.scopes}`, {
                method: 'POST',
                headers: { "Authorization": "Bearer " + accessTokenLocal },
                body
            });
            const data = answer.data;

            if (data === undefined) {
                throw new Error(GENERIC_UPLOAD_FAILURE);
            }

            if (tokenExpired(answer)) {
                const newAccess = await getAccessTokenWithRefresh();
                if (newAccess) {
                    setAccessToken(newAccess);
                    localStorage.setItem('accessToken', newAccess);
                    return await uploadOne(file, newAccess);
                } else {
                    throw new Error("La sesión expiró. Inicie sesión de nuevo.");
                }
            }

            if (hasCode(answer, 'file_uploaded')) {
                return true;
            }

            // Why it was refused, from the back's list of problems or, while
            // that change waits on this reader, from the old map.
            const { messages, unknownPrograms } = scopeRefusal(answer);
            if (unknownPrograms) {
                downloadProgramErrors(unknownPrograms);
            }
            if (messages.length > 0) {
                throw new Error(messages.join(' '));
            }

            throw new Error(GENERIC_UPLOAD_FAILURE);
        };

        const results = [];
        for (const f of filesToUpload) {
            try {
                await uploadOne(f, access);
                results.push({ file: f.name, ok: true });
            } catch (e) {
                // Why this file was rejected, in Spanish: it is shown, one
                // line per file, rather than counted and dropped.
                results.push({ file: f.name, ok: false, reason: e.message });
            }
        }

        setShowLoader(false);

        const summary = uploadSummary(results, emailValue);
        if (!summary) return;

        if (summary.severity === "success") {
            hideScope();
        }
        setMessage(summary.message);
        setSeverity(summary.severity);
        setTimeout(() => { setShowAlert(true) }, 0);
    };

    const downloadTemplate = async () => {
        setShowLoader(true);
        const template = evaluationOption === "post" ? DOCUMENTS.scopeTemplatePost : DOCUMENTS.scopeTemplatePre;
        const saved = await fetchDocument(template, saveAs, { accessToken, setAccessToken, getAccessTokenWithRefresh });
        setShowLoader(false);
        if (!saved) {
            // A plain link said nothing when the object answered 403: the
            // browser downloaded the error page under the template's name.
            setMessage("No fue posible descargar el template. Intente de nuevo más tarde.");
            setSeverity("danger");
            setTimeout(() => { setShowAlert(true) }, 0);
        }
    }

    return (
        <>
            <div className={ScopeStyle["scope"]} id="scope">
                <div className={ScopeStyle["layer-hide-menu"]} onClick={() => hideScope()}></div>
                <div className={ScopeStyle["scope-container"]} id="scope-container">
                    <div className={ScopeStyle["scope-header"]}>
                        <h2>Generador de consultas</h2>
                    </div>
                    <div className={ScopeStyle["scope-container-scroll"]}>
                        <form id="contact-form">
                            <label htmlFor="email">Email</label>
                            <input type="email" id="email" name="email" placeholder="ejemplo@dominio.com" required />
                            <div style={{ width: "94%", margin: "auto", marginBottom: "8px" }}>
                                <label style={{ marginLeft: "0px" }}>
                                    <input type="radio" value="new" checked={universeOption === "new"} onChange={(e) => { setUniverseOption(e.target.value); setKindOption('program_name'); setTableOption('rcn'); setWeightOption('last_day') }} />
                                    &ensp;Modelo 1&emsp;&ensp;
                                </label>
                                <label style={{ marginLeft: "0px" }}>
                                    <input type="radio" value="claro" checked={universeOption === "claro"} onChange={(e) => { setUniverseOption(e.target.value); setKindOption('program_name'); setTableOption('rcn'); setWeightOption('last_day') }} />
                                    &ensp;Universo Claro
                                </label>
                            </div>
                            <div style={{ borderBottom: "1px solid #cccccc", width: "94%", margin: "auto", marginTop: "6px", marginBottom: "14px" }} />
                            {universeOption === 'new' ?
                                <>
                                    <div style={{ width: "94%", margin: "auto", marginBottom: "8px" }}>
                                        <label style={{ marginLeft: "0px" }}>
                                            <input type="radio" value="last_day" checked={weightOption === "last_day"} onChange={(e) => { setWeightOption(e.target.value) }} />
                                            &ensp;Ponderadores último día&emsp;&ensp;
                                        </label>
                                    </div>
                                    <div style={{ borderBottom: "1px solid #cccccc", width: "94%", margin: "auto", marginTop: "6px", marginBottom: "14px" }} />
                                </>
                                :
                                null}
                            <div style={{ width: "94%", margin: "auto", marginBottom: "8px" }}>
                                <label style={{ marginLeft: "0px" }}>
                                    <input type="radio" value="evaluacion-pauta" checked={selectedOption === "evaluacion-pauta"} onChange={(e) => setSelectedOption(e.target.value)} />
                                    &ensp;Evaluación de pauta&emsp;&ensp;
                                </label>
                                <label style={{ marginLeft: "0px" }}>
                                    <input type="radio" value="reach-neto" checked={selectedOption === "reach-neto"} onChange={(e) => setSelectedOption(e.target.value)} />
                                    &ensp;Reach neto
                                </label>
                            </div>
                            <div style={{ borderBottom: "1px solid #cccccc", width: "94%", margin: "auto", marginTop: "6px", marginBottom: "14px" }} />
                            {selectedOption === "evaluacion-pauta" ?
                                <>
                                    <div style={{ display: "flex", marginTop: "2px", marginBottom: "-5px" }}>
                                        <label>Target personalizado</label>
                                        <input type="checkbox" id="custom-target" className={ScopeStyle["switch-input"]} checked={customTarget} onChange={() => {
                                            setCustomTarget(!customTarget);
                                            setTargetSelected(targets.map((v, i) => ({ value: i, label: v })));
                                        }} />
                                        <label style={{ marginLeft: "10px", marginBottom: "0px" }} htmlFor="custom-target" role="switch" aria-checked={customTarget}></label>
                                    </div>
                                    {customTarget ?
                                        <div style={{ width: "94%", margin: "auto", marginTop: "10px", marginBottom: "12px" }}>
                                            <Select
                                                menuPlacement="auto"
                                                options={[
                                                    { label: 'Género', options: targets.slice(0, 2).map((v, i) => ({ value: i, label: v })) },
                                                    { label: 'Edad', options: targets.slice(2, 8).map((v, i) => ({ value: i + 2, label: v })) },
                                                    { label: 'Región', options: targets.slice(8, 14).map((v, i) => ({ value: i + 8, label: v })) },
                                                    { label: 'Estrato', options: targets.slice(14, 20).map((v, i) => ({ value: i + 14, label: v })) },
                                                    { label: 'Universo', options: targets.slice(20, 22).map((v, i) => ({ value: i + 20, label: v })) },
                                                    { label: 'Comprador ppal', options: targets.slice(22).map((v, i) => ({ value: i + 22, label: v })) }
                                                ]}
                                                placeholder="Construya el target deseado"
                                                isSearchable
                                                isMulti
                                                value={targetSelected}
                                                onChange={(e) => setTargetSelected(e)}
                                            />
                                        </div>
                                        : null}
                                    <div style={{ borderBottom: "1px solid #cccccc", width: "94%", margin: "auto", marginTop: "6px", marginBottom: "14px" }} />
                                    <div style={{ width: "94%", margin: "auto", marginBottom: "8px" }}>
                                        <label style={{ marginLeft: "0px" }}>
                                            <input type="radio" value="pre" checked={evaluationOption === "pre"} onChange={(e) => setEvaluationOption(e.target.value)} />
                                            &ensp;Pre evaluación&emsp;&ensp;
                                        </label>
                                        <label style={{ marginLeft: "0px" }}>
                                            <input type="radio" value="post" checked={evaluationOption === "post"} onChange={(e) => setEvaluationOption(e.target.value)} />
                                            &ensp;Post evaluación
                                        </label>
                                    </div>
                                    {evaluationOption === "pre" ?
                                        <>
                                            <div style={{ width: "94%", margin: "auto", marginTop: "10px" }} />
                                            <label>Rango fechas</label>
                                            <div style={{ display: "flex", width: "94%", margin: "auto", marginBottom: "12px", alignItems: "center" }}>
                                                <span style={{ marginLeft: "2px", marginRight: "10px" }}>Del</span>
                                                <input
                                                    type="date"
                                                    min={new Date(new Date().setDate(new Date().getDate() - 366)).toJSON().slice(0, 10)}
                                                    max={new Date(new Date().setDate(new Date().getDate() - 1)).toJSON().slice(0, 10)}
                                                    value={lowerPreDate || ""}
                                                    onChange={(e) => setLowerPreDate(e.target.value)}
                                                />
                                                <span style={{ marginLeft: "10px", marginRight: "10px" }}>al</span>
                                                <input
                                                    type="date"
                                                    min={weightOption !== 'const' ? toDateInputValue(dataRangeDates["min_fecha"]) : new Date(2024, 11, 31).toJSON().slice(0, 10)}
                                                    max={toDateInputValue(dataRangeDates["max_fecha"])}
                                                    value={upperPreDate || ""}
                                                    onChange={(e) => setUpperPreDate(e.target.value)}
                                                />
                                            </div>
                                        </>
                                        : null}
                                    <div style={{ width: "94%", margin: "auto", marginTop: "10px" }} />
                                    <label htmlFor="file-input">Archivo</label>
                                    <div className={ScopeStyle["drag-and-drop"]}>
                                        <div
                                            id="drop-zone"
                                            className={ScopeStyle["drop-zone"]}
                                            onClick={() => openFileInput()}
                                            // React's handlers, not listeners installed once at mount:
                                            // those kept the first render's file list, so a second
                                            // drop replaced the first, and stayed on the old node when
                                            // the zone was drawn again after Reach neto.
                                            onDragOver={handleDragOver}
                                            onDrop={handleFileSelect}
                                        >
                                            Arrastra y suelta o haz clic aquí para seleccionar un archivo.
                                            <input
                                                type="file"
                                                id="file-input"
                                                ref={fileInputRef}
                                                className={ScopeStyle["scope-file-input"]}
                                                name="file-input"
                                                multiple
                                                accept=".xlsx"
                                                onChange={(e) => handleFileSelect(e)}
                                            />
                                        </div>
                                        {/* Drawn from the state, so it shows what GENERAR uploads,
                                            also after the zone is drawn again (Reach neto and back). */}
                                        <p id="file-name">
                                            {filesToUpload.map((f, i) => (
                                                <React.Fragment key={`${f.name}__${f.size}`}>
                                                    {i > 0 ? <br /> : null}
                                                    <span>{f.name}</span>
                                                    <button
                                                        type="button"
                                                        className={ScopeStyle["file-remove"]}
                                                        title={`Quitar ${f.name}`}
                                                        aria-label={`Quitar ${f.name}`}
                                                        onClick={() => removeFile(f)}
                                                    >
                                                        ×
                                                    </button>
                                                </React.Fragment>
                                            ))}
                                        </p>
                                    </div>
                                </>
                                :
                                null}
                        </form>
                        {/* Inside the scroll, with the rest of the form: it was a sibling of it,
                            so the reach neto fields never scrolled and squeezed the part that did. */}
                        {selectedOption === 'reach-neto' ?
                            <div id="reach-neto">
                                {universeOption === 'new' ?
                                    <>
                                        <div style={{ width: "94%", margin: "auto", marginBottom: "8px" }}>
                                            <label style={{ marginLeft: "0px" }}>
                                                <input type="radio" value="rcn" checked={tableOption === "rcn"} onChange={(e) => { setTableOption(e.target.value); setSelectedChannelPrograms([]) }} />
                                                &ensp;Privados nacional&emsp;&ensp;
                                            </label>
                                            <label style={{ marginLeft: "0px" }}>
                                                <input type="radio" value="publics" checked={tableOption === "publics"} onChange={(e) => { setTableOption(e.target.value); setSelectedChannelPrograms([]) }} />
                                                &ensp;Públicos&emsp;&ensp;
                                            </label>
                                            <label style={{ marginLeft: "0px" }}>
                                                <input type="radio" value="warner" checked={tableOption === "warner"} onChange={(e) => { setTableOption(e.target.value); setSelectedChannelPrograms([]) }} />
                                                &ensp;Warner&emsp;&ensp;
                                            </label>
                                            <label style={{ marginLeft: "0px" }}>
                                                <input type="radio" value="win" checked={tableOption === "win"} onChange={(e) => { setTableOption(e.target.value); setSelectedChannelPrograms([]) }} />
                                                &ensp;Win&emsp;&ensp;
                                            </label>
                                            <label style={{ marginLeft: "0px" }}>
                                                <input type="radio" value="claro" checked={tableOption === "claro"} onChange={(e) => { setTableOption(e.target.value); setSelectedChannelPrograms([]) }} />
                                                &ensp;Claro
                                            </label>
                                        </div>
                                        <div style={{ borderBottom: "1px solid #cccccc", width: "94%", margin: "auto", marginTop: "14px", marginBottom: "14px" }} />
                                        <div style={{ width: "94%", margin: "auto", marginBottom: "8px" }}>
                                            <label style={{ marginLeft: "0px" }}>
                                                <input type="radio" value="program_name" checked={kindOption === "program_name"} onChange={(e) => { setKindOption(e.target.value); setSelectedChannelPrograms([]) }} />
                                                &ensp;Programas&emsp;&ensp;
                                            </label>
                                            <label style={{ marginLeft: "0px" }}>
                                                <input type="radio" value="time_group" checked={kindOption === "time_group"} onChange={(e) => { setKindOption(e.target.value); setSelectedChannelPrograms([]) }} />
                                                &ensp;Franjas horarias
                                            </label>
                                        </div>
                                        <div style={{ borderBottom: "1px solid #cccccc", width: "94%", margin: "auto", marginTop: "14px", marginBottom: "14px" }} />
                                    </>
                                    :
                                    null}
                                <label>Rango fechas</label>
                                <div style={{ display: "flex", width: "94%", margin: "auto", marginTop: "10px", marginBottom: "12px", alignItems: "center" }}>
                                    <span style={{ marginLeft: "2px", marginRight: "10px" }}>Del</span>
                                    <input
                                        type="date"
                                        min={toDateInputValue(dataRangeDates["min_fecha"])}
                                        max={toDateInputValue(dataRangeDates["max_fecha"])}
                                        value={lowerDate || ""}
                                        onChange={(e) => setLowerDate(e.target.value)}
                                    />
                                    <span style={{ marginLeft: "10px", marginRight: "10px" }}>al</span>
                                    <input
                                        type="date"
                                        min={weightOption !== 'const' ? toDateInputValue(dataRangeDates["min_fecha"]) : new Date(2024, 11, 31).toJSON().slice(0, 10)}
                                        max={toDateInputValue(dataRangeDates["max_fecha"])}
                                        value={upperDate || ""}
                                        onChange={(e) => setUpperDate(e.target.value)}
                                    />
                                </div>    
                                {kindOption === 'program_name' ?
                                    <>
                                        <div style={{ display: "flex", marginTop: "2px", marginBottom: "-5px" }}>
                                            <label>Programas unificados</label>
                                            <input type="checkbox" id="unified-programmes" className={ScopeStyle["switch-input"]} checked={unifiedPrograms} onChange={() => { setUnifiedPrograms(!unifiedPrograms); setSelectedChannelPrograms([]) }} />
                                            <label style={{ marginLeft: "10px", marginBottom: "0px" }} htmlFor="unified-programmes" role="switch" aria-checked={unifiedPrograms}></label>

                                            {combinedOffered(netReachForm) ?
                                                <>
                                                    <label>Combinado</label>
                                                    <input type="checkbox" id="combined-programmes" className={ScopeStyle["switch-input"]} checked={combinedPrograms} onChange={() => { setCombinedPrograms(!combinedPrograms) }} />
                                                    <label style={{ marginLeft: "10px", marginBottom: "0px" }} htmlFor="combined-programmes" role="switch" aria-checked={combinedPrograms}></label>
                                                </>
                                                : null}
                                        </div>
                                        <div style={{ width: "94%", margin: "auto", marginTop: "10px", marginBottom: "12px" }} />
                                        <label>Canal y Programa</label>
                                        <div style={{ width: "94%", margin: "auto", marginTop: "10px", marginBottom: "12px" }}>
                                            <Select
                                                menuPlacement="auto"
                                                options={channelPrograms}
                                                placeholder="Seleccione canal y programa"
                                                isSearchable
                                                isMulti
                                                isDisabled = {!hasDateRange}
                                                isOptionDisabled={() => selectedChannelPrograms.length >= channelProgramLimit(netReachForm)}
                                                value={selectedChannelPrograms}
                                                onChange={(e) => setSelectedChannelPrograms(e)}
                                            />
                                        </div>
                                    </>
                                    :
                                    <>
                                        <label>Canal y Franja horaria</label>
                                        <div style={{ width: "94%", margin: "auto", marginTop: "10px", marginBottom: "12px" }}>
                                            <Select
                                                menuPlacement="auto"
                                                options={[...new Set(channelPrograms.map(c => c['label'].split(' - ')[0]))]
                                                    .flatMap(d => ['All Day', 'Full Day', 'Over', 'Day', 'Early', 'Prime', 'Late'].map(v => d + ' - ' + v))
                                                    .map((v, i) => ({ value: i, label: v }))}
                                                placeholder="Seleccione canal y franja horaria"
                                                isSearchable
                                                isMulti
                                                isOptionDisabled={() => selectedChannelPrograms.length >= channelProgramLimit(netReachForm)}
                                                value={selectedChannelPrograms}
                                                onChange={(e) => setSelectedChannelPrograms(e)}
                                            />
                                        </div>
                                    </>}
                            
                                <label>Días de la semana</label>
                                <div style={{ width: "94%", margin: "auto", marginTop: "10px", marginBottom: "12px" }}>
                                    <Select
                                        menuPlacement="auto"
                                        options={dOWList}
                                        placeholder="Seleccione los días a consultar"
                                        isSearchable
                                        isMulti
                                        value={dOWOptions}
                                        onChange={(e) => setDOWOptions(e)}
                                    />
                                </div>
                                <label>Población</label>
                                <div style={{ width: "94%", margin: "auto", marginTop: "10px", marginBottom: "12px" }}>
                                    <Select
                                        menuPlacement="auto"
                                        options={populationList}
                                        placeholder="Seleccione población objetivo"
                                        isSearchable
                                        isClearable
                                        value={populationOption}
                                        onChange={(e) => setPopulationOption(e)}
                                    />
                                </div>
                            </div>
                            :
                            null}
                    </div>
                    <div className={ScopeStyle["scope-button-container"]}>
                        {selectedOption === "evaluacion-pauta" & evaluationOption === "pre" ? <button style={{ right: '60%' }} className={ScopeStyle["scope-button-cancel"]} onClick={() => hideScope()}>CANCELAR</button> : null}
                        {selectedOption === "evaluacion-pauta" ? (evaluationOption === "post" ? <button className={ScopeStyle["scope-button-cancel"]} onClick={() => hideScope()}>CANCELAR</button> : <button className={ScopeStyle["scope-button-download-2"]} onClick={() => downloadChannelProgramList()}>LISTA PROGRAMAS</button>) : null}
                        {selectedOption === "evaluacion-pauta" ? <button id="scope-template" className={ScopeStyle["scope-button-template"]} onClick={() => downloadTemplate()}>{evaluationOption === "pre" ? "TEMPLATE PRE" : "TEMPLATE POST"}</button> : <button className={ScopeStyle["scope-button-cancel"]} style={{ right: "20%" }} onClick={() => hideScope()}>CANCELAR</button>}
                        <button id="scope-generate" className={ScopeStyle["scope-button-download"]} onClick={() => selectedOption === "evaluacion-pauta" ? generateScope() : generateReach()}>GENERAR</button>
                    </div>
                </div>
            </div>
        </>
    );
}

export default Scope;
